import React from 'react'
import BenefitSection from './BenefitSection'
import ServiceCard from './ServiceCard'
import ScheduleDialog from './ScheduleDialog'
import { useBenefits } from '../BenefitsProvider'
import { useTranslation } from '../../../hooks/useTranslation'
import CoachServicesPage from './CoachServicesPage'
import { SERVICE_REQUEST_ICONS } from '../ServiceRequestIcons'
import NetworkingStrategyDialog from './NetworkingStrategyDialog'
import ResumeTailoringDialog from './ResumeTailoringDialog'

const PremiumCoachServicesPage = () => {
  const { current_user, coach, serviceRequestList } = useBenefits()

  const { isLiaison, liaison_image_url } = coach

  const { t } = useTranslation()

  const networkingStrategyDialogShow = !(
    serviceRequestList['ServiceRequest::JobSearchStrategy']?.completed ||
    serviceRequestList['ServiceRequest::LinkedInReview']?.completed
  )

  const resumeTailoringDialogShow =
    !serviceRequestList['ServiceRequest::ResumeReview']?.completed

  const introductionCallCompleted =
    serviceRequestList['ServiceRequest::OrientationCall']?.completed

  const introductionCallScheduled =
    serviceRequestList['ServiceRequest::OrientationCall']?.status ===
    'scheduled'

  const services = {
    coach_intro: [
      {
        title: t('service_card.coach_introduction.title'),
        description: introductionCallCompleted
          ? t('service_card.coach_introduction.completed_description')
          : introductionCallScheduled
            ? t('service_card.coach_introduction.meeting_scheduled_description')
            : t('service_card.coach_introduction.description'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::OrientationCall'],
        requestType: 'ServiceRequest::OrientationCall',
        classes: 'bg-catskill-white-100',
        fullWidth: true,
        coachCard: true,
        buttonText: t('service_card.coach_introduction.button_text'),
        photoUrl: isLiaison
          ? liaison_image_url
          : coach.transparent_photo || coach.photo,
        liaison: isLiaison,
        buttonUrl: '/coach_services/professional_services/orientation_call',
      },
    ],
    early: [
      {
        requestType: 'ServiceRequest::ResumeReview',
        title: t('service_card.resume_development.title'),
        description: t('service_card.resume_development.description'),
        buttonText: t('service_card.resume_development.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::ResumeReview'],
        classes: 'bg-willow-brook-100',
        buttonUrl: '/coach_services/resume_review',
      },
      {
        requestType: 'ServiceRequest::ResumeTailoring',
        title: t('service_card.resume_tailoring.title'),
        description: t('service_card.resume_tailoring.description'),
        buttonText: serviceRequestList['ServiceRequest::ResumeTailoring']
          ?.completed
          ? t('service_card.schedule_again')
          : t('service_card.resume_tailoring.button_text'),
        buttonChild: ResumeTailoringDialog,
        buttonChildShow: resumeTailoringDialogShow,
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::ResumeTailoring'],
        classes: 'bg-willow-brook-100',
        buttonUrl: '/coach_services/professional_services/resume_tailoring',
      },
      {
        requestType: 'ServiceRequest::CoverLetterReview',
        title: t('service_card.cover_letter_review.title'),
        description: t('service_card.cover_letter_review.description'),
        buttonText: serviceRequestList['ServiceRequest::CoverLetterReview']
          ?.completed
          ? t('service_card.schedule_again')
          : t('service_card.cover_letter_review.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::CoverLetterReview'],
        classes: 'bg-willow-brook-100',
        buttonUrl: '/coach_services/professional_services/cover_letter_review',
      },
      {
        requestType: 'ServiceRequest::LinkedInReview',
        title: t('service_card.linkedin_optimization.title'),
        description: t('service_card.linkedin_optimization.description'),
        buttonText: t('service_card.linkedin_optimization.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::LinkedInReview'],
        classes: 'bg-ecru-white-50',
        buttonUrl: '/coach_services/professional_services/linked_in_review',
      },
      {
        requestType: 'ServiceRequest::JobSearchStrategy',
        title: t('service_card.job_search_strategy.title'),
        description: t('service_card.job_search_strategy.description'),
        buttonText: serviceRequestList['ServiceRequest::JobSearchStrategy']
          ?.completed
          ? t('service_card.schedule_again')
          : t('service_card.job_search_strategy.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::JobSearchStrategy'],
        classes: 'bg-ecru-white-50',
        buttonUrl: '/coach_services/professional_services/job_search_strategy',
      },
      {
        requestType: 'ServiceRequest::TargetCompanyResearch',
        title: t('service_card.target_company_research.title'),
        description: t('service_card.target_company_research.description'),
        buttonText: t('service_card.target_company_research.button_text'),
        iconType:
          SERVICE_REQUEST_ICONS['ServiceRequest::TargetCompanyResearch'],
        classes: 'bg-ecru-white-50',
        buttonUrl:
          '/coach_services/professional_services/target_company_research',
      },
      {
        requestType: 'ServiceRequest::CareerPivotPlanning',
        title: t('service_card.career_pivot_planning.title'),
        description: t('service_card.career_pivot_planning.description'),
        buttonText: t('service_card.career_pivot_planning.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::CareerPivotPlanning'],
        classes: 'bg-catskill-white-100',
        fullWidth: true,
        photoUrl:
          'https://www-yns-com.s3.amazonaws.com/images/coach_services/career_pivot_planning.png',
        buttonUrl:
          '/coach_services/professional_services/career_pivot_planning',
      },
    ],
    midway: [
      {
        requestType: 'ServiceRequest::NetworkingStrategy',
        title: t('service_card.networking_strategy.title'),
        description: t('service_card.networking_strategy.description'),
        buttonText: t('service_card.networking_strategy.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::NetworkingStrategy'],
        fullWidth: true,
        classes: 'bg-willow-brook-100',
        buttonChild: NetworkingStrategyDialog,
        buttonChildShow: networkingStrategyDialogShow,
        buttonUrl: '/coach_services/professional_services/networking_strategy',
      },
      {
        requestType: 'ServiceRequest::InterviewPrep',
        title: t('service_card.interview_prep.title'),
        description: t('service_card.interview_prep.description'),
        buttonText: t('service_card.interview_prep.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::InterviewPrep'],
        classes: 'bg-willow-brook-100',
        buttonUrl: '/coach_services/professional_services/interview_prep',
      },
      {
        requestType: 'ServiceRequest::MockInterview',
        title: t('service_card.mock_interview.title'),
        description: t('service_card.mock_interview.description'),
        buttonText: serviceRequestList['ServiceRequest::MockInterview']
          ?.completed
          ? t('service_card.schedule_again')
          : t('service_card.mock_interview.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::MockInterview'],
        classes: 'bg-willow-brook-100',
        buttonUrl: '/coach_services/professional_services/mock_interview',
      },
    ],
    closing: [
      {
        requestType: 'ServiceRequest::NegotiationStrategy',
        title: t('service_card.negotiation_strategy.title'),
        description: t('service_card.negotiation_strategy.description'),
        buttonText: serviceRequestList['ServiceRequest::NegotiationStrategy']
          ?.completed
          ? t('service_card.schedule_again')
          : t('service_card.negotiation_strategy.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::NegotiationStrategy'],
        classes: 'bg-willow-brook-100',
        fullWidth: true,
        buttonUrl: '/coach_services/professional_services/negotiation_strategy',
      },
      {
        requestType: 'ServiceRequest::JobSearchBurnout',
        title: t('service_card.job_search_burnout.title'),
        description: t('service_card.job_search_burnout.description'),
        buttonText: t('service_card.job_search_burnout.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::JobSearchBurnout'],
        classes: 'bg-willow-brook-100',
        buttonUrl: '/coach_services/professional_services/job_search_burnout',
      },
      {
        requestType: 'ServiceRequest::OnboardingSupport',
        title: t('service_card.onboarding_support.title'),
        description: t('service_card.onboarding_support.description'),
        buttonText: serviceRequestList['ServiceRequest::OnboardingSupport']
          ?.completed
          ? t('service_card.schedule_again')
          : t('service_card.onboarding_support.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::OnboardingSupport'],
        classes: 'bg-willow-brook-100',
        buttonUrl: '/coach_services/professional_services/onboarding_support',
      },
      {
        requestType: 'ServiceRequest::RetirementSupport',
        title: t('service_card.retirement_support.title'),
        description: t('service_card.retirement_support.description'),
        buttonText: serviceRequestList['ServiceRequest::RetirementSupport']
          ?.completed
          ? t('service_card.schedule_again')
          : t('service_card.retirement_support.button_text'),
        iconType: SERVICE_REQUEST_ICONS['ServiceRequest::RetirementSupport'],
        classes: 'bg-catskill-white-100',
        fullWidth: true,
        photoUrl:
          'https://www-yns-com.s3.amazonaws.com/images/coach_services/retirement_support.png',
        buttonUrl: '/coach_services/professional_services/retirement_support',
      },
    ],
  }
  return (
    <CoachServicesPage>
      <BenefitSection className="mb-6">
        <div className="grid grid-cols-1">
          {services.coach_intro.map((service, idx) => (
            <ServiceCard key={`${service.requestType}-${idx}`} {...service} />
          ))}
        </div>
      </BenefitSection>

      <BenefitSection
        className="mt-6 border-t border-[#B9B9B9] pt-6"
        title={t('early_services.title')}
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {services.early.map((service, idx) => (
            <ServiceCard key={`${service.requestType}-${idx}`} {...service} />
          ))}
        </div>
      </BenefitSection>

      <BenefitSection
        title={t('midway_services.title')}
        className="mt-6 border-t border-[#B9B9B9] pt-6"
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {services.midway.map((service, idx) => (
            <ServiceCard key={`${service.requestType}-${idx}`} {...service} />
          ))}
        </div>
      </BenefitSection>

      <BenefitSection
        title={t('closing_services.title')}
        className="mt-6 border-t border-[#B9B9B9] pt-6"
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {services.closing.map((service, idx) => (
            <ServiceCard key={`${service.requestType}-${idx}`} {...service} />
          ))}
        </div>
      </BenefitSection>

      <ScheduleDialog current_user={current_user} coach={coach} />
    </CoachServicesPage>
  )
}

export default PremiumCoachServicesPage
